import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import Button from '@components/atoms/ui-kit/button.tsx';
import { useNavigate } from 'react-router-dom';
import { useForgotPassword } from '@utils/api/users/use-forgot-password.tsx';
import { css, StyleSheet } from 'aphrodite';
import { useThemeContext } from '@utils/hooks/use-theme-context.ts';
import cn from 'classnames';
import Input from '@components/atoms/ui-kit/input.tsx';
import PublicLogo from '@components/shared/public-logo.tsx';
import { useMemo } from 'react';

type FormValues = { username: string };

export default function ResetPassword() {
  const { t } = useTranslation();
  const { getColour } = useThemeContext();

  const schema = yup.object({
    username: yup.string().required(t('username-is-required'))
  });

  const navigate = useNavigate();
  const mutation = useForgotPassword();

  const styles = useMemo(
    () =>
      StyleSheet.create({
        mainContainer: {
          backgroundColor: getColour('secondLayer')
        },
        mainText: {
          color: getColour('normalText')
        },
        iconsColor: {
          color: getColour('icon')
        }
      }),
    [getColour]
  );

  const {
    register,
    handleSubmit,
    formState: { errors }
  } = useForm<FormValues>({
    resolver: yupResolver(schema)
  });

  const onSubmit = async (data: FormValues) => {
    if (!mutation.isSuccess) {
      mutation.mutate({ email: data.username });
    }
  };

  return (
    <div
      className={cn(
        'bg-app flex items-center justify-center align-middle mx-auto py-[15%] w-full min-h-screen',
        css(styles.mainText)
      )}
    >
      <div className="mx-auto w-full max-w-sm lg:w-96 flex flex-col gap-y-8">
        <div className="flex flex-col gap-y-5 justify-center items-center">
          <PublicLogo className="h-auto w-[11rem]" />
          <h1 className="text-2xl font-bold leading-9 tracking-tight ">
            {t('password-reset')}
          </h1>
          <p className="text-sm text-center items-center my-3">
            {t('password-reset-description')}
          </p>
        </div>
        <div className="w-full flex flex-col gap-y-6 mx-auto">
          <form
            action="#"
            method="POST"
            className="space-y-6"
            onSubmit={handleSubmit(onSubmit)}
          >
            <div className="w-full flex flex-col gap-y-3">
              <Input
                id="username"
                type="text"
                autoComplete="username"
                label={t('username')}
                labelClass="block text-sm font-medium leading-6 text-center"
                placeholder=""
                wrapperClass="w-full"
                className="text-center"
                error={errors.username && errors.username.message}
                {...register('username')}
              />
            </div>
            <div className="flex justify-center">
              <Button
                type="submit"
                className="w-full"
                variant={'primary'}
                label={
                  mutation.isLoading
                    ? t('sending-request')
                    : mutation.isSuccess
                      ? t('request-sent')
                      : t('request-password-reset')
                }
                disabled={mutation.isLoading || mutation.isSuccess}
              />
            </div>
          </form>
          {mutation.isSuccess && (
            <p className="text-sm text-green-600 text-center">
              {t('password-reset-request-sent')}
            </p>
          )}
          <div className="relative">
            <div
              className="absolute inset-0 flex items-center"
              aria-hidden="true"
            >
              <div className="w-full border-t border-gray-200 dark:border-zinc-700" />
            </div>
            <div className="relative flex justify-center text-sm font-medium leading-6">
              <span className="bg-white dark:bg-zinc-950 px-6 text-gray-900 dark:text-zinc-100">
                {t('or-go-back-to-login')}
              </span>
            </div>
          </div>
          <div className="flex justify-center w-full">
            <Button
              label={t('login')}
              className="min-w-full"
              onClick={() => {
                navigate('/login');
              }}
              variant="secondary"
            />
          </div>
        </div>
      </div>
    </div>
  );
}
